// client/src/components/pages/VehicleRouteTracker/VehicleSearch.tsx (converted from
// VehicleSearch.js, ST-00242)
//
// The registration picker. The page used to offer only a search box: nothing showed until the
// user had typed two characters of a registration they had to know already. It now lists the
// trucks seen in the selected period (GET /vehicle-tracker/vehicles: the caller's tenant owns
// the dispatch or receipt end of their tickets), most recently seen first, as soon as the field
// is focused. Typing filters that list in the browser, on any part of the registration, so a
// list of hundreds narrows at once. A registration that is not in the list (a truck outside the
// period, or past the listed limit) can still be looked up by typing it in full: "Look up …"
// appears once no listed truck matches the text (or always, when the list is cut or missing).
//
// Built on react-select, like ThemedSelect, with the same theme-token styles, so the field and
// its menu follow the light and dark themes and keep react-select's keyboard handling and
// combobox semantics.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import CreatableSelect from 'react-select/creatable';
import {
  components,
  type ControlProps,
  type CSSObjectWithLabel,
  type FormatOptionLabelMeta,
  type InputActionMeta,
  type MenuListProps,
  type StylesConfig,
} from 'react-select';
import { FaClock, FaSearch, FaTicketAlt, FaTruck } from 'react-icons/fa';
import api from '../../../services/api';
import { formatMiningDate } from '../../../utils/dateUtils';
import { themedSelectStyles, themedSelectTheme } from '../../../utils/selectStyles';
import type { VehicleListItem } from '../../../types/clientPages/vehicleRouteTracker';

/** Most options the menu renders at once; typing narrows the rest. */
export const MAX_SHOWN = 100;

export interface VehicleSearchProps {
  onSelect: (regNo: string) => void;
  selectedVehicle: string | null;
  /** The period, YYYY-MM-DD; the list waits until both are set. */
  startDate: string | null;
  endDate: string | null;
}

interface VehicleOption {
  value: string;
  label: string;
  /** The listed truck; null for the selected value when it was typed in, and for "Look up". */
  vehicle: VehicleListItem | null;
  __isNew__?: boolean;
}

type ListState = 'idle' | 'loading' | 'ready' | 'error' | 'too-long';

/** A registration as typed text is compared to it: no spaces or dashes, upper case. */
export const normaliseReg = (reg: string): string => reg.replace(/[\s-]+/g, '').toUpperCase();

/** A typed registration as it is looked up: trimmed, upper case, single spaces. */
export const typedReg = (input: string): string => input.trim().replace(/\s+/g, ' ').toUpperCase();

/**
 * The vehicles whose registration holds `query`: those starting with it first, then those
 * holding it anywhere, each group in list order (most recently seen first).
 */
export function matchVehicles(
  vehicles: readonly VehicleListItem[],
  query: string
): VehicleListItem[] {
  const q = normaliseReg(query);
  if (!q) return vehicles.slice();
  const starts: VehicleListItem[] = [];
  const holds: VehicleListItem[] = [];
  for (const v of vehicles) {
    const reg = normaliseReg(v.regNo);
    if (reg.startsWith(q)) starts.push(v);
    else if (reg.includes(q)) holds.push(v);
  }
  return starts.concat(holds);
}

const toOption = (vehicle: VehicleListItem): VehicleOption => ({
  value: vehicle.regNo,
  label: vehicle.regNo,
  vehicle,
});

/** The menu's own note under the options: how many are shown, and whether the list is cut. */
interface MenuNote {
  shown: number;
  matched: number;
  truncated: boolean;
  limit: number;
}
const MenuNoteContext = createContext<MenuNote | null>(null);

const noteStyle = {
  padding: '0.5rem 0.75rem',
  fontSize: '0.75rem',
  color: 'var(--text-muted)',
  borderTop: '1px solid var(--border)',
} as const;

function VehicleMenuList(props: MenuListProps<VehicleOption, false>) {
  const note = useContext(MenuNoteContext);
  const more = note ? note.matched - note.shown : 0;
  return (
    <components.MenuList {...props}>
      {props.children}
      {note && more > 0 && (
        <div style={noteStyle}>
          {`Showing ${note.shown} of ${note.matched.toLocaleString()}. Keep typing to narrow the list.`}
        </div>
      )}
      {note?.truncated && (
        <div style={noteStyle}>
          {`Only the ${note.limit.toLocaleString()} most recently seen vehicles are listed. Type a full registration to look up another.`}
        </div>
      )}
    </components.MenuList>
  );
}

function VehicleControl({ children, ...props }: ControlProps<VehicleOption, false>) {
  return (
    <components.Control {...props}>
      <FaSearch aria-hidden="true" className="ml-4 w-4 h-4 flex-shrink-0 text-slate-400" />
      {children}
    </components.Control>
  );
}

const SELECT_COMPONENTS = { MenuList: VehicleMenuList, Control: VehicleControl };

// The page's main field: taller and rounder than a filter dropdown, as the search box was.
const baseStyles = themedSelectStyles as unknown as StylesConfig<VehicleOption, false>;
const styles: StylesConfig<VehicleOption, false> = {
  ...baseStyles,
  control: (base, state) => ({
    ...(baseStyles.control ? baseStyles.control(base, state) : base),
    minHeight: '52px',
    borderRadius: '0.75rem',
    fontSize: '1rem',
  }),
  valueContainer: (base, state) => ({
    ...(baseStyles.valueContainer ? baseStyles.valueContainer(base, state) : base),
    padding: '0 0.75rem',
  }),
  menu: (base, state) => ({
    ...(baseStyles.menu ? baseStyles.menu(base, state) : base),
    borderRadius: '0.75rem',
    overflow: 'hidden',
  }),
  menuPortal: (base: CSSObjectWithLabel) => ({ ...base, zIndex: 9999 }),
};

function OptionLabel({
  option,
  meta,
}: {
  option: VehicleOption;
  meta: FormatOptionLabelMeta<VehicleOption>;
}): ReactNode {
  if (meta.context === 'value') {
    return (
      <span className="inline-flex items-center gap-2 font-semibold tracking-wide">
        <FaTruck aria-hidden="true" className="w-3.5 h-3.5 text-primary-500" />
        {option.value}
      </span>
    );
  }
  const { vehicle } = option;
  if (!vehicle) {
    // The "Look up …" row (CreatableSelect's new option) carries its label already.
    return <span className="font-medium">{option.label}</span>;
  }
  return (
    <span className="flex items-center gap-3">
      <span className="flex-1 min-w-0">
        <span className="block font-bold tracking-wide">{vehicle.regNo}</span>
        <span className="flex items-center gap-1 text-xs" style={{ opacity: 0.75 }}>
          <FaTicketAlt aria-hidden="true" className="w-3 h-3" />
          {`${(vehicle.ticketCount || 0).toLocaleString()} ticket${vehicle.ticketCount === 1 ? '' : 's'}`}
        </span>
      </span>
      {vehicle.lastSeen && (
        <span className="flex-shrink-0 text-right text-xs" style={{ opacity: 0.75 }}>
          <span className="block">Last seen</span>
          <span className="inline-flex items-center gap-1 font-medium">
            <FaClock aria-hidden="true" className="w-3 h-3" />
            {formatMiningDate(vehicle.lastSeen)}
          </span>
        </span>
      )}
    </span>
  );
}

/** Whether an error is the server's "period too long to list" answer. */
function isPeriodTooLong(err: unknown): boolean {
  const res = (err as { response?: { status?: number; data?: { code?: string } } }).response;
  return res?.status === 400 && res.data?.code === 'PERIOD_TOO_LONG';
}

function isCancel(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name;
  return name === 'CanceledError' || name === 'AbortError';
}

const VehicleSearch = ({ onSelect, selectedVehicle, startDate, endDate }: VehicleSearchProps) => {
  const [vehicles, setVehicles] = useState<VehicleListItem[]>([]);
  const [listState, setListState] = useState<ListState>('idle');
  const [truncated, setTruncated] = useState(false);
  const [limit, setLimit] = useState(0);
  const [input, setInput] = useState('');

  // The period's vehicles, again whenever the period changes.
  useEffect(() => {
    if (!startDate || !endDate) {
      setVehicles([]);
      setListState('idle');
      return undefined;
    }
    const controller = new AbortController();
    setListState('loading');
    api.vehicleTracker
      .listVehicles(startDate, endDate, { signal: controller.signal })
      .then((res) => {
        setVehicles(res.data.vehicles ?? []);
        setTruncated(!!res.data.truncated);
        setLimit(res.data.limit ?? 0);
        setListState('ready');
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return;
        const tooLong = isPeriodTooLong(err);
        if (!tooLong) console.error('Error listing vehicles:', err);
        setVehicles([]);
        setTruncated(false);
        setListState(tooLong ? 'too-long' : 'error');
      });
    return () => controller.abort();
  }, [startDate, endDate]);

  const known = useMemo(() => new Set(vehicles.map((v) => normaliseReg(v.regNo))), [vehicles]);
  const matches = useMemo(() => matchVehicles(vehicles, input), [vehicles, input]);
  const options = useMemo(() => matches.slice(0, MAX_SHOWN).map(toOption), [matches]);
  const note = useMemo<MenuNote>(
    () => ({ shown: options.length, matched: matches.length, truncated, limit }),
    [options.length, matches.length, truncated, limit]
  );

  const value = useMemo<VehicleOption | null>(() => {
    if (!selectedVehicle) return null;
    const vehicle = vehicles.find((v) => v.regNo === selectedVehicle) ?? null;
    return { value: selectedVehicle, label: selectedVehicle, vehicle };
  }, [selectedVehicle, vehicles]);

  const onInputChange = (next: string, meta: InputActionMeta) => {
    // Registrations are upper case; react-select clears the text itself on blur and on a pick.
    setInput(meta.action === 'input-change' ? next.toUpperCase() : next);
  };

  const noOptionsMessage = ({ inputValue }: { inputValue: string }): string => {
    const lookUp = 'Type a registration to look it up.';
    if (listState === 'error') return `Couldn't load the vehicles of this period. ${lookUp}`;
    if (listState === 'too-long')
      return `Choose a period of a year or less to list its vehicles. ${lookUp}`;
    if (inputValue) return 'Type at least 2 characters to look up a registration.';
    if (listState === 'idle') return 'Choose a period to list its vehicles.';
    return `No vehicles have tickets in this period. ${lookUp}`;
  };

  const plural = vehicles.length === 1 ? '' : 's';
  const status =
    listState === 'loading'
      ? 'Loading the vehicles of this period…'
      : listState === 'ready'
        ? `${vehicles.length.toLocaleString()}${truncated ? '+' : ''} vehicle${plural} seen in this period. Pick one, or type to filter.`
        : listState === 'too-long'
          ? 'Choose a period of a year or less to list its vehicles, or type a registration.'
          : listState === 'error'
            ? "Couldn't load the vehicles of this period. You can still type a registration."
            : '';

  return (
    <div>
      <label htmlFor="vehicle-registration" className="sr-only">
        Vehicle registration
      </label>
      <MenuNoteContext.Provider value={note}>
        <CreatableSelect<VehicleOption, false>
          inputId="vehicle-registration"
          classNamePrefix="select"
          components={SELECT_COMPONENTS}
          styles={styles}
          theme={themedSelectTheme}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
          maxMenuHeight={340}
          options={options}
          value={value}
          inputValue={input}
          onInputChange={onInputChange}
          // The list is filtered above (matchVehicles), so react-select shows it as given.
          filterOption={null}
          onChange={(opt) => {
            if (opt && !opt.__isNew__) onSelect(opt.value);
          }}
          onCreateOption={(text) => {
            const reg = typedReg(text);
            if (reg) onSelect(reg);
          }}
          isValidNewOption={(text) => {
            const reg = typedReg(text);
            if (reg.length < 2 || known.has(normaliseReg(reg))) return false;
            // While listed trucks match, the text is a filter. A full list that matches nothing
            // means no tickets in the period; a cut or missing list may simply not hold it.
            return matches.length === 0 || listState !== 'ready' || truncated;
          }}
          formatCreateLabel={(text) => `Look up “${typedReg(text)}”`}
          createOptionPosition="last"
          formatOptionLabel={(option, meta) => <OptionLabel option={option} meta={meta} />}
          isLoading={listState === 'loading'}
          loadingMessage={() => 'Loading the vehicles of this period…'}
          noOptionsMessage={noOptionsMessage}
          openMenuOnFocus
          placeholder="Pick a vehicle, or type a registration (e.g. LFX826MP)"
        />
      </MenuNoteContext.Provider>
      <p className="mt-1.5 px-1 text-xs text-slate-500" aria-live="polite">
        {status}
      </p>
    </div>
  );
};

export default VehicleSearch;
